import { Tag } from '@/types/tag'
import request from '@/utils/request'

const baseUrl = '/cems'

export interface Company {
  id: number
  name: string
  address: string
  telephone: string
  sispekServer: string
  appId: string
  appSecret: string
  timeOffset: string
}

export class Company {
  id = 0
  name = ''
  address = ''
  telephone = ''
  sispekServer = ''
  appId = ''
  appSecret = ''
  timeOffset = ''
}

export interface StatusStack {
  id: number
  status: string
  description: string
  value: number
  enabled: boolean
}

export interface StatusStackLogic {
  id: number
  stackUid: string
  statusId: number
  description: string
  order: number
  enabled: boolean
  logic: string
  status: StatusStack
}

export interface Stack {
  id: number
  uid: string
  name: string
  alias: string
  capacity: string
  collection: number
  height: string
  diameter: string
  fuel: string
  latitude: string
  longitude: string
  sulfur: string
  synchronSchedule: string
  synchron: boolean
  syncAvgGroup: string
  syncRetryCount: number
  syncRetryInterval: number
  maxResendCount: number
  sendNotifOnSyncSuccess: boolean
  syncUTCOffset: string
  parameterMap: string
  qaqcId: number
  QAQCEnabled: boolean
  QAQCReferece: string
  //eslint-disable-next-line
  dataLogger: any
}

export interface Stack {
  parameters?: Parameter[]
  sispek?: Sispek
  siplah?: Siplah
}

export class Stack {
  public id = 0
  public uid = ''
  public name = ''
  public alias = ''
  public capacity = ''
  public height = ''
  public diameter = ''
  public fuel = ''
  public latitude = ''
  public longitude = ''
  public sulfur = ''
  public synchronSchedule = ''
  public synchron = false
  public syncAvgGroup = ''
  public syncRetryCount = 0
  public syncRetryInterval = 0
  public maxResendCount = 0
  public sendNotifOnSyncSuccess = false
  public syncUTCOffset = ''
  public parameterMap = ''
  public qaqcId = 0
  public QAQCEnabled = false
  public QAQCReferece = ''
}

export interface CEMSReport {
  fields: string[]
  values: number[]
  company: Company
  stack: Stack
}

export function GenerateReport(
  source: string,
  stackUID: string,
  reportId: number,
  timeStart: number,
  timeEnd: number,
  download: boolean
  // eslint-disable-next-line
): Promise<any> {
  return request.post(
    `${baseUrl}/report`,
    {
      source,
      stackUID,
      reportId,
      timeStart,
      timeEnd,
      download,
    },
    { responseType: 'blob' }
  )
}

export function FindStatusStack(): Promise<StatusStack[]> {
  return request.get(`${baseUrl}/status-stack-list`)
}
export function FindStatusStackLogic(uid: string): Promise<StatusStackLogic[]> {
  return request.get(`${baseUrl}/${uid}/status-stack-logic`)
}
export function PatchStatusStackLogic(
  stackUID: string,
  logicID: number,
  logic: StatusStackLogic
): Promise<StatusStack[]> {
  return request.patch(
    `${baseUrl}/${stackUID}/status-stack-logic/${logicID}`,
    logic
  )
}

export function ReorderStatusStackLogic(
  logics: StatusStackLogic[]
): Promise<StatusStack[]> {
  return request.patch(`${baseUrl}/status-stack-logic/reorder`, logics)
}

export function DownloadReport(
  source: string,
  stackUID: string,
  reportId: number,
  timeStart: number,
  timeEnd: number
): Promise<CEMSReport> {
  return request.post(`${baseUrl}/report`, {
    source,
    stackUID,
    reportId,
    timeStart,
    timeEnd,
    download: true,
  })
}

export function GetCompany(): Promise<Company> {
  return request.get(`${baseUrl}/company`)
}

export function PatchCompany(id: number, c: Company): Promise<Company> {
  return request.patch(`${baseUrl}/company/${id}`, c)
}

export function CreateStack(stack: Stack) {
  return request.post(`${baseUrl}/stack`, stack)
}

/**
 *
 * @param includes Attributes to be includes (case sensitive).
 * Available attributes: DataLogger, Sispek, Siplah
 * @param stacksName Name of stacks
 * @returns
 */
export function FindStacks(
  includes?: string[],
  stacksName?: string[]
): Promise<Stack[]> {
  let incl = ''
  if (includes && includes.length > 0) {
    incl = includes.map(encodeURIComponent).join(',')
  }
  let stackNames = ''
  if (stacksName && stacksName.length > 0) {
    stackNames = stacksName.map(encodeURIComponent).join(',')
  }
  return request.get(`${baseUrl}/stack?includes=${incl}&stack=${stackNames}`)
}

export function GetStack(uid: string): Promise<Stack> {
  return request.get(`${baseUrl}/stack/${uid}`)
}

export function PatchStack(uid: string, stack: Stack) {
  return request.patch(`${baseUrl}/stack/${uid}`, stack)
}

/*--------------------------------------------------------------------------------------------
  SISPEK
---------------------------------------------------------------------------------------------*/
export interface SispekAccount {
  id: number
  version: string
  baseUrl: string
  appId: string
  appSecret: string
  description: string
}

export interface SispekParameter {
  id: number
  sispekId: number
  function: string
  parameter: string
  source: string
  valueBroken: number
  valueCalibration: number
  valueStop: number
}

export interface Sispek {
  id: number
  stackId: number
  enabled: boolean
  average: string
  schedule: string
  retryCount: number
  retryIntervalSeconds: number
  serverTimeZone: string
  timeFieldAlias: string
  timeFormat: string
  utcOffset: string
  successNotif: boolean
  resendCount: number
  parameters?: SispekParameter[]
}

export interface ParameterMap {
  id: number
  chimneyId: number
  function: string
  source: string
  destination: string
}

export function GetSispekAccount(): Promise<SispekAccount> {
  return request.get(`${baseUrl}/sispek/account`)
}

export function PatchSispekAccount(
  account: SispekAccount
): Promise<SispekAccount> {
  return request.patch(`${baseUrl}/sispek/account`, account)
}

export function GetStackWithSispek(uid: string): Promise<Stack> {
  return request.get(`${baseUrl}/${uid}/sispek`)
}

export interface PatchSispekRequest {
  deletedParameter: number[]
  stack: Stack
}
export function PatchSispek(
  uid: string,
  deletedParameter: number[],
  stack: Stack
): Promise<Stack> {
  return request.patch(`${baseUrl}/${uid}/sispek`, { deletedParameter, stack })
}

export interface SispekRequiredParameter {
  name: string
}

export function GetSISPEKChimneysCode(): Promise<string[]> {
  return request.get(`${baseUrl}/sispek/chimneys`)
}

export function GetSISPEKRequiredParameters(
  stackUID: string
): Promise<SispekRequiredParameter[]> {
  return request.get(`${baseUrl}/${stackUID}/sispek/required-parameters`)
}

/**
 * Melihat data pengukuran yang sudah diformat dalam format JSON untuk SISPEK
 * @param stackUid UID of stack
 * @param measurementTime Time on milliseconds
 * @returns
 */
export function ViewSispekJSON(stackUid: string, measurementTime: number) {
  return request.post(`${baseUrl}/sispek/json`, { stackUid, measurementTime })
}

export interface SubmitToSispekResponse {
  success: boolean
  message: string
  type: string
}

/**
 * Mengirim data ke SISPEK secara manual
 * @param stackUid UID of stack
 * @param measurementTime Time on milliseconds
 * @returns
 */
export function SubmitToSispek(
  stackUid: string,
  measurementTime: number
): Promise<SubmitToSispekResponse> {
  return request.post(`${baseUrl}/sispek/submit`, { stackUid, measurementTime })
}

export function FindSispekLogsMonthly(stackUid: string, date: Date) {
  return request.post(`${baseUrl}/sispek/logs-monthly`, { stackUid, date })
}

export function FindSispekTimeline(stackUid: string, date: Date) {
  return request.post(`${baseUrl}/sispek/timeline`, { stackUid, date })
}
/*--------------------------------------------------------------------------------------------
  SIPLAH
---------------------------------------------------------------------------------------------*/
export interface SiplahAccount {
  id: number
  baseUrl: string
  apiKey: string
  description: string
}

export interface SiplahParameter {
  id: number
  siplahId: number
  function: string
  parameter: string
  source: string
  valueBroken: number
  valueCalibration: number
  valueStop: number
}

export interface Siplah {
  id: number
  code: string
  uid: string
  stackId: number
  enabled: boolean
  average: string
  schedule: string
  retryCount: number
  retryIntervalSeconds: number
  serverTimeZone: string
  timeFieldAlias: string
  timeFormat: string
  utcOffset: string
  successNotif: boolean
  resendCount: number
  parameters?: SiplahParameter[]
}

export interface SiplahRequiredParameter {
  nama: string
}

export function GetSiplahAccount(): Promise<SiplahAccount> {
  return request.get(`${baseUrl}/siplah/account`)
}

export function PatchSiplahAccount(
  account: SiplahAccount
): Promise<SiplahAccount> {
  return request.patch(`${baseUrl}/siplah/account`, account)
}

export function GetSIPLAHStacks() {
  return request.get(`${baseUrl}/siplah/stacks`)
}

export function GetStackUIDFromSiplah(code: string): Promise<string> {
  return request.get(`${baseUrl}/siplah/get-stack-uid?code=${code}`)
}

export function GetSIPLAHRequiredParameters(
  stackUID: string
): Promise<SiplahRequiredParameter[]> {
  return request.get(`${baseUrl}/${stackUID}/siplah/required-parameters`)
}

export function GetStackWithSiplah(uid: string): Promise<Stack> {
  return request.get(`${baseUrl}/${uid}/siplah`)
}

export interface PatchSiplahRequest {
  deletedParameter: number[]
  stack: Stack
}

export function PatchSiplah(
  uid: string,
  deletedParameter: number[],
  stack: Stack
): Promise<Stack> {
  return request.patch(`${baseUrl}/${uid}/siplah`, { deletedParameter, stack })
}

/**
 * Melihat data pengukuran yang sudah diformat dalam format JSON untuk SIPLAH
 * @param stackUid UID of stack
 * @param measurementTime Time on milliseconds
 * @returns
 */
export function ViewSiplahJSON(stackUid: string, measurementTime: number) {
  return request.post(`${baseUrl}/siplah/json`, { stackUid, measurementTime })
}

export interface SubmitToSiplahResponse {
  success: boolean
  message: string
}

/**
 * Mengirim data ke SIPLAH secara manual
 * @param stackUid UID of stack
 * @param measurementTime Time on milliseconds
 * @returns
 */
export function SubmitToSiplah(
  stackUid: string,
  measurementTime: number
): Promise<SubmitToSiplahResponse> {
  return request.post(`${baseUrl}/siplah/submit`, { stackUid, measurementTime })
}

export function FindSiplahLogsMonthly(stackUid: string, date: Date) {
  return request.post(`${baseUrl}/siplah/logs-monthly`, { stackUid, date })
}

export function FindSiplahTimeline(stackUid: string, date: Date) {
  return request.post(`${baseUrl}/siplah/timeline`, { stackUid, date })
}

export function FindActionHistory(
  stacks: string[],
  parameters: string[],
  actions: string[],
  start: number,
  end: number
): Promise<any[]> {
  return request.get(`${baseUrl}/siplah/action`, {
    params: {
      stacks: stacks.join(';'),
      parameters: parameters.join(';'),
      actions: actions.join(';'),
      start,
      end,
    },
  })
}

/*--------------------------------------------------------------------------------------------
  SISPEK OLD
---------------------------------------------------------------------------------------------*/

export function GetStackRequiredParams(id: number): Promise<string[]> {
  return request.post(`${baseUrl}/sispek/${id}/required-params`)
}

export function SubmitParameter(
  id: number,
  periode: number
): Promise<string[]> {
  return request.post(`${baseUrl}/sispek/submit-parameter`, {
    stackId: id,
    periode,
  })
}

export function GetSISPEKJson(id: number, periode: number): Promise<string[]> {
  return request.post(`${baseUrl}/sispek/json`, {
    stackId: id,
    periode,
  })
}

export interface TestResult {
  status: boolean
  message: string
}

export function SISPEKConnectionTest(): Promise<TestResult> {
  return request.post(`${baseUrl}/sispek/test`)
}

export interface SyncLog {
  createdAt: Date
  updatedAt: Date
  chimney: string
  dataTime: Date
  success: boolean
  message: string
  sendCount: number
}

export interface LogsPagination {
  count: number
  limit: number
  offset: number
  data: SyncLog[]
}
export function FindSyncLogs(
  offset: number,
  limit: number,
  where?: { [key: string]: string | number }
): Promise<LogsPagination> {
  let url: string

  if (where) {
    const query = new URLSearchParams(
      // eslint-disable-next-line
      (where as any) as Record<string, string>
    ).toString()
    url = `${baseUrl}/sync-log?offset=${offset}&limit=${limit}&${query}`
  } else {
    url = `${baseUrl}/sync-log?offset=${offset}&limit=${limit}`
  }
  return request.get(url)
}

export function FindSyncMonth(where: {
  [key: string]: string | number
}): Promise<LogsPagination> {
  const query = new URLSearchParams(
    // eslint-disable-next-line
    (where as any) as Record<string, string>
  ).toString()

  return request.get(`${baseUrl}/sync-log?${query}`)
}

export function FindSyncTimeline(where: {
  [key: string]: string | number
}): Promise<LogsPagination> {
  const query = new URLSearchParams(
    // eslint-disable-next-line
    (where as any) as Record<string, string>
  ).toString()

  return request.get(`${baseUrl}/sync-timeline?${query}`)
}

/*-----------------------------------------------------------------
Parameter
-----------------------------------------------------------------*/

export interface InfluxSeries {
  name: string
  columns: string[]
  // eslint-disable-next-line
  values: any[][]
}

export interface InfluxResult {
  Messages: string | null
  statement_id: number
  Series: InfluxSeries[]
}

export interface Parameter {
  id: number
  createdAt: Date
  updatedAt: Date
  stackUid: string
  uid: string
  name: string
  nameHTML?: string
  engineeringUnit?: string
  engineeringUnitHTML?: string
  precision: number
  standar?: number
  standarEnabled?: boolean
  tagUid: string
  log: boolean
  status: StatusParameter
  limitHigh?: number
  limitHighNotif?: boolean
  limitHighHigh?: number
  limitHighHighNotif?: boolean
  simulation?: boolean
  simulationMin?: number
  simulationMax?: number
  simulationMode?: 'random' | 'increment' | 'decrement'
  min?: number
  max?: number
  color?: string
  deviceID?: number
  tagAddress?: string
  tagDataType?: string
  tag: Tag
  // eslint-disable-next-line
  value?: any
  exceed?: boolean
  exceedDuration?: number
  expression: string

  records?: InfluxResult
}

export interface StatusParameter {
  id: number
  createdAt?: Date
  updatedAt?: Date
  deletedAt?: Date
  status: string
  description: string
}

export function GetListOfStatusParameter(): Promise<StatusParameter[]> {
  return request.get(`${baseUrl}/parameter/list-status`)
}

export function CreateParameter(parameter: Parameter) {
  return request.post(`${baseUrl}/parameter`, parameter)
}
export function FindParameter(stackUid: string): Promise<Parameter[]> {
  return request.get(`${baseUrl}/${stackUid}/parameter`)
}

export function GetParameter(uid: string): Promise<Parameter> {
  return request.get(`${baseUrl}/parameter/${uid}`)
}

export function PatchParameter(
  uid: string,
  parameter: Parameter
): Promise<Parameter> {
  return request.patch(`${baseUrl}/parameter/${uid}`, parameter)
}

export function DeleteParameter(uid: string): Promise<Parameter> {
  return request.delete(`${baseUrl}/parameter/${uid}`)
}

/*-----------------------------------------------------------------
RECORD
-----------------------------------------------------------------*/
export interface RecordRequest {
  stackUID: string
  avg: string
  from: string
  to: string
  parameters: string[]
}

// function isNumeric(n: string) {
//   return !isNaN(parseFloat(n))
// }
export function ReadRecord(payload: RecordRequest): Promise<InfluxResult> {
  // if (isNumeric(payload.from)) {
  //   payload.from = (parseInt(payload.from) * 1000000).toString()
  // }
  // if (isNumeric(payload.to)) {
  //   payload.to = (parseInt(payload.to) * 1000000).toString()
  // }
  return request.post(`${baseUrl}/record`, payload)
}

/*-----------------------------------------------------------------
QAQC
-----------------------------------------------------------------*/
export interface QAQCParameter {
  id: number
  createdAt: Date
  updatedAt: Date
  deletedAt: null | Date
  name: string
}

export interface QAQC {
  id: number
  createdAt: Date
  updatedAt: Date
  deletedAt: null | Date
  name: string
  description: string
  parameters: QAQCParameter[]
}

export function CreateQAQC(qaqc: QAQC) {
  return request.post(`${baseUrl}/qaqc`, qaqc)
}

export function GetQAQC(id: number): Promise<QAQC> {
  return request.get(`${baseUrl}/qaqc/${id}`)
}

export function FindQAQC(): Promise<QAQC[]> {
  return request.get(`${baseUrl}/qaqc`)
}

export function PatchQAQC(id: number, qaqc: QAQC, deleted: number[]) {
  return request.patch(`${baseUrl}/qaqc/${id}`, { qaqc, deleted })
}

export function DeleteQAQC(id: number): Promise<QAQC> {
  return request.delete(`${baseUrl}/qaqc/${id}`)
}

export interface QAQCNormalData {
  id?: number
  [key: string]: number | undefined
}

export function FindNormalData(
  qaqcId: number
): Promise<{ qaqc: QAQC; normalData: QAQCNormalData[] }> {
  return request.get(`${baseUrl}/qaqc/${qaqcId}/normal`)
}

export function SaveNormalData(
  qaqcId: number,
  normalsData: QAQCNormalData[],
  fields: string[],
  deleted: number[]
) {
  return request.post(`${baseUrl}/qaqc/${qaqcId}/normal`, {
    normalData: normalsData,
    fields,
    deleted,
  })
}

export function FindQAQCParameters(qaqcId: number): Promise<QAQCParameter[]> {
  return request.get(`${baseUrl}/qaqc/${qaqcId}/parameters`)
}

/* --------------------------------------------------------

ALARM

----------------------------------------------------------*/
export interface AckNote {
  ID: number
  Timestamp: Date
  Message: string
}

export interface Alarm {
  ID: number
  UID: string
  Status: string
  StartTime: Date
  EndTime: Date
  AckTime: Date
  StartValue: number
  EndValue: number
  AckValue: number
  AckUser: string
  AckNoteID: number
  Message: string
  AckNote?: AckNote
}

export interface AlarmQuery {
  StartTime: number
  EndTime: number
  Status: string
  Message: string
}

export function FindAlarms(q: AlarmQuery): Promise<Alarm[]> {
  return request.post(`${baseUrl}/alarm`, q)
}

export function AckAll() {
  return request.post(`${baseUrl}/alarm/ackAll`)
}
